import { motion } from "framer-motion";
import { ArrowRight, Circle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TopBar } from "@/components/layout/TopBar";
import { cn } from "@/lib/cn";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { statusLabel, statusPillClass } from "@/lib/projectStatus";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RoleProject {
  project_id: string;
  project_name: string;
  phase: string;
  weekly_hours: number;
  health: string | null;
  priority: string | null;
  pct_complete: number;
}

interface RolePerson {
  name: string;
  capacity_hrs_week: number;
  demand_hrs_week: number;
  utilization_pct: number;
}

interface RoleCard {
  role_key: string;
  supply_hrs_week: number;
  demand_hrs_week: number;
  free_hrs_week: number;
  utilization_pct: number;
  status: string;
  people: RolePerson[];
  projects: RoleProject[];
}

interface PersonAssignmentItem {
  entity_kind: "project";
  id: string;
  name: string;
  role_key: string;
  status: string | null;
  hours_per_week: number;
}

interface PersonAssignments {
  person_name: string;
  capacity_hrs_week: number;
  total_demand_hrs_week: number;
  utilization_pct: number;
  items: PersonAssignmentItem[];
}

function usePersonAssignments(personName: string | null) {
  return useQuery<PersonAssignments>({
    queryKey: ["capacity", "person-assignments", personName],
    queryFn: () =>
      api
        .get("/capacity/person-assignments", { params: { person_name: personName } })
        .then((r) => r.data),
    enabled: !!personName,
  });
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLE_LABEL: Record<string, string> = {
  pm: "Project Manager",
  ba: "Business Analyst",
  functional: "Functional",
  technical: "Technical",
  developer: "Developer",
  infrastructure: "Infrastructure",
  dba: "DBA",
  erp: "ERP Consultant",
  operations: "Operations",
};

const PHASE_LABEL: Record<string, string> = {
  planning: "Planning",
  execution: "Execution",
  testing_go_live: "Testing / Go Live",
};

const PHASE_BADGE: Record<string, string> = {
  planning: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",
  execution: "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
  testing_go_live: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
};

const PHASE_DOT: Record<string, string> = {
  planning: "bg-sky-400",
  execution: "bg-amber-400",
  testing_go_live: "bg-emerald-400",
};

function stateColor(pctUsed: number) {
  if (pctUsed >= 1.0) return { bar: "bg-red-500", text: "text-red-700 dark:text-red-300", pill: "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300", label: "Over" };
  if (pctUsed >= 0.8) return { bar: "bg-amber-400", text: "text-amber-800 dark:text-amber-300", pill: "bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300", label: "Stretched" };
  if (pctUsed >= 0.5) return { bar: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-300", pill: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", label: "Healthy" };
  return { bar: "bg-sky-400", text: "text-sky-700 dark:text-sky-300", pill: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300", label: "Under-used" };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

function useRoleCards() {
  return useQuery<{ cards: RoleCard[] }>({
    queryKey: ["capacity", "role-cards"],
    queryFn: () => api.get("/capacity/role-cards").then((r) => r.data),
  });
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RoleCards() {
  const { data, isLoading, isError, error } = useRoleCards();
  const navigate = useNavigate();
  // Click a person on a role card → deep-link to /people/:name (the
  // dedicated person view replaced the modal that used to live here).
  const onSelectPerson = (name: string) =>
    navigate(`/people/${encodeURIComponent(name)}`);

  // Only show roles with supply or demand
  const cards = (data?.cards ?? []).filter(
    (c) => c.supply_hrs_week > 0 || c.demand_hrs_week > 0,
  );

  return (
    <>
      <TopBar
        title="Role Overview"
        subtitle="Per-role capacity cards. Click a person to see what they're assigned to."
      />
      <div className="p-8">
        {isLoading && (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            Loading role cards...
          </div>
        )}
        {isError && (
          <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">
            {(error as Error).message}
          </div>
        )}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {cards.map((card, i) => (
            <RoleCardItem
              key={card.role_key}
              card={card}
              index={i}
              onSelectPerson={onSelectPerson}
            />
          ))}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Card Component
// ---------------------------------------------------------------------------

function RoleCardItem({ card, index, onSelectPerson }: {
  card: RoleCard;
  index: number;
  onSelectPerson: (name: string) => void;
}) {
  const sc = stateColor(card.utilization_pct);
  const pctWidth = Math.min(100, card.utilization_pct * 100);

  // Split projects into current (in-phase) and up next
  const currentProjects = (card.projects ?? []).filter(
    (p) => p.pct_complete < 1.0 && p.weekly_hours > 0,
  );

  // Group by phase for display
  const phases = ["planning", "execution", "testing_go_live"];
  const byPhase: Record<string, RoleProject[]> = {};
  for (const phase of phases) {
    const inPhase = currentProjects.filter((p) => p.phase === phase);
    if (inPhase.length > 0) byPhase[phase] = inPhase;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05, ease: "easeOut" }}
      className="rounded-xl border border-line bg-surface p-5 shadow-sm"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm font-semibold text-ink">
            {ROLE_LABEL[card.role_key] ?? card.role_key}
          </div>
          <div className="mt-0.5 text-[11px] text-ink-muted">
            {card.supply_hrs_week}h/wk capacity
          </div>
        </div>
        <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", sc.pill)}>
          {sc.label}
        </span>
      </div>

      {/* Hero row */}
      <div className="mt-4 flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
            Free this week
          </div>
          <div className={cn("text-3xl font-bold tabular-nums", sc.text)}>
            {card.free_hrs_week}
            <span className="ml-0.5 text-base font-semibold text-ink-subtle">h</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
            Allocated
          </div>
          <div className={cn("text-xl font-bold tabular-nums", sc.text)}>
            {Math.round(card.utilization_pct * 100)}
            <span className="ml-0.5 text-sm font-semibold text-ink-subtle">%</span>
          </div>
        </div>
      </div>

      {/* Usage bar */}
      <div className="mt-2">
        <div className="h-2 overflow-hidden rounded-full bg-raised">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pctWidth}%` }}
            transition={{ duration: 0.6, delay: 0.25 + index * 0.05, ease: "easeOut" }}
            className={cn("h-full rounded-full", sc.bar)}
          />
        </div>
        <div className="mt-1 flex items-center justify-between text-[10px] tabular-nums text-ink-subtle">
          <span>{card.demand_hrs_week}h demand</span>
          <span>of {card.supply_hrs_week}h capacity</span>
        </div>
      </div>

      {/* Team — the individuals who make up this role, each with
          their own load. Sorted most-loaded first so an imbalanced
          role (role at 40% but one person at 90%) is visible at a
          glance without a drill-down. */}
      {(card.people ?? []).length > 0 && (
        <div className="mt-5">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
            Team ({(card.people ?? []).length})
          </div>
          <div className="space-y-1.5">
            {(card.people ?? []).map((person) => {
              const psc = stateColor(person.utilization_pct);
              const pw = Math.min(100, person.utilization_pct * 100);
              return (
                <button
                  type="button"
                  key={person.name}
                  onClick={() => onSelectPerson(person.name)}
                  title="See what they're assigned to"
                  className="-mx-1.5 flex w-[calc(100%+0.75rem)] items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-raised"
                >
                  <span className="w-32 shrink-0 truncate text-xs text-ink-2 hover:text-brand-navy-700 dark:hover:text-brand-navy-300">
                    {person.name}
                  </span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-raised">
                    <div
                      className={cn("h-full rounded-full", psc.bar)}
                      style={{ width: `${Math.max(2, pw)}%` }}
                    />
                  </div>
                  <span className={cn("w-10 shrink-0 text-right text-[10px] font-semibold tabular-nums", psc.text)}>
                    {Math.round(person.utilization_pct * 100)}%
                  </span>
                  <span className="w-16 shrink-0 text-right text-[10px] tabular-nums text-ink-subtle">
                    {person.demand_hrs_week}/{person.capacity_hrs_week}h
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Projects by phase */}
      <div className="mt-5 space-y-4">
        {phases.map((phase) => {
          const projects = byPhase[phase];
          if (!projects) return null;
          return (
            <div key={phase}>
              <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
                <span className={cn("h-2 w-2 rounded-full", PHASE_DOT[phase] ?? "bg-muted-strong")} />
                {PHASE_LABEL[phase] ?? phase}
                <span className="ml-auto font-normal text-ink-faint">{projects.length}</span>
              </div>
              <div className="space-y-1.5">
                {projects.map((p) => (
                  <div
                    key={p.project_id}
                    className="flex items-center gap-2 rounded-lg border border-line-soft bg-canvas/60 px-2.5 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium text-ink">
                        {p.project_name}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5">
                        <span
                          className={cn(
                            "rounded-full px-1.5 py-0.5 text-[9px] font-medium",
                            PHASE_BADGE[p.phase] ?? "bg-raised text-ink-muted",
                          )}
                        >
                          {PHASE_LABEL[p.phase] ?? p.phase}
                        </span>
                        {p.priority && (
                          <span className="text-[9px] text-ink-subtle">{p.priority}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right tabular-nums">
                      <div className="text-xs font-semibold text-ink-2">
                        {p.weekly_hours}h
                      </div>
                      <div className="text-[9px] text-ink-subtle">/wk</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {currentProjects.length === 0 && (
          <div className="rounded-lg border border-dashed border-line px-3 py-3 text-center text-xs text-ink-subtle">
            No active projects
          </div>
        )}
      </div>
    </motion.div>
  );
}

